<template>
  <div>
    <div
      class="loading-ui width-expand flex-middle text-center flex-center padding-l"
      v-if="asyncStatus.asyncDataLoading">
      <spinner :size="4"></spinner>
    </div>
    <div v-else>
      <p v-if="!versions || versions?.length < 1" class="h3 no-items">
        {{ tu("core.versionManager.noItemsFound") }}
      </p>

      <div v-if="versions && versions?.length > 0">
        <table class="table table--highlight width-expand overflow-x-scroll">
          <caption style="display:none;">{{ tu("core.versionManager.versionTableCaption") }}</caption>
          <thead>
            <tr>
              <th>{{ tu("core.versionManager.columns.id") }}</th>
              <th>{{ tu("core.versionManager.columns.createdAt") }}</th>
              <th>{{ tu("core.versionManager.columns.name") }}</th>
              <th>{{ tu("core.versionManager.columns.type") }}</th>
              <th>{{ tu("core.versionManager.columns.wasAccepted") }}</th>
              <th>{{ tu("core.versionManager.columns.actions") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="version of versions">
              <td>
                {{ version.id }}
              </td>
              <td>
                {{ translateDate(version.createdAt, "dateWithTime") }}
              </td>
              <td>
                <div
                  v-tooltip="{
                    content: sanitizeHtml(version.name),
                    autoTranslate: false,
                  }">
                  <span class="block max-width-200 text-overflow-ellipsis">
                    {{ version.name }}
                  </span>
                </div>
              </td>
              <td>
                {{ tu(`entity.contentVersion.types.${version.versionType}`) }}
              </td>
              <td>
                <span v-if="version.wasAccepted"><icon icon="check" theme="success"></icon></span>
                <span v-else="version.wasAccepted"><icon icon="x" theme="danger"></icon></span>
              </td>
              <td>
                <div class="flex-middle flex-center gap-m">
                  <form-button
                    :text="false"
                    :slim="true"
                    theme="danger"
                    class="square"
                    v-tooltip="'core.versionManager.actions.deleteVersion'"
                    @click="startDeleteVersionFlow(version)"
                    icon="trash"></form-button>
                  <form-button
                    :text="false"
                    :slim="true"
                    theme="inverse"
                    class="square"
                    v-tooltip="'core.versionManager.actions.viewVersion'"
                    @click="showVersion(version)"
                    icon="eye"></form-button>

                  <form-button
                    :text="false"
                    :slim="true"
                    theme="success"
                    class="square"
                    v-tooltip="'core.versionManager.actions.acceptVersion'"
                    @click="startAcceptVersionFlow(version)"
                    icon="check"></form-button>

                  <form-button
                    :text="false"
                    :slim="true"
                    theme="lead"
                    class="square"
                    v-tooltip="'core.versionManager.actions.acceptAndDeleteVersion'"
                    @click="startAcceptVersionAndDeleteFlow(version)"
                    icon="clipboard-check"></form-button>


                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <modal
      :show="versionToShow"
      size="xl"
      @modal:closed="versionToShow = false"
      title="core.versionManager.viewVersion.modalTitle">
      <template #default>
        <crud-view
          :api-get-item-url="computedGetItemRoute"
          :form-path="computedGetFormPath"
          :back-route="closeViewVersionModal"
          :item-data-mutator="(data) => data.item"
          :toolbar-attrs="{ style: 'display: none' }"></crud-view>
      </template>
      <template #footer>
        <div class="width-expand flex-center flex-middle gap-xl flex-wrap">
          <form-button
            text="core.versionManager.viewVersion.closePreview"
            icon="chevron-inline-start"
            theme="inverse"
            @click="versionToShow = false"></form-button>

          <form-button
            text="core.versionManager.viewVersion.acceptVersion"
            icon="check"
            theme="success"
            @click="startAcceptVersionFlow(versionToShow)"></form-button>

          <form-button
            text="core.versionManager.viewVersion.acceptAndDeleteVersion"
            icon="check"
            theme="lead"
            @click="startAcceptVersionAndDeleteFlow(versionToShow)"></form-button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import asyncComponentFactory from "@/client/extensions/modules/asyncComponentFactory";

export default {
  props: {
    entityType: {
      type: String,
      default: "",
    },
    id: {
      type: [String, Number],
      default: 0,
    },
    /**
     * If the version model doesnt follow convension
     */
    versionEntityType: {
      type: [String, Boolean],
      default: false,
    },
    getItemUrl: {
      type: [String, Function, Boolean],
      default: false,
    },
    getFormConfigUrl: {
      type: [String, Function, Boolean],
      default: false,
    },
  },
  emits: [
    "versionManager:versionAccepted",
    "versionManager:versionDeleted",
  ],
  components: {
    CrudView: asyncComponentFactory("components/crud/View.vue", true),
  },
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  data() {
    return {
      versionToShow: false,
      versions: false,
      asyncData: {
        versions: {
          target: `entity/${this.entityType}/${this.id}/version`,
          data: {},
          options: {
            dataMutator: (data) => {
              return data?.items;
            },
            errorHandler: (a, b) => {
              console.log("version manager async data error handler", a, b);
            },
          },
        },
      },
    };
  },
  methods: {
    async startDeleteVersionFlow(version) {
      try {
        await this.$s.ui.modal.confirm(
          "core.versionManager.deleteVersion.confirmText"
        );
      } catch (e) {
        return this.$s.ui.notification(
          "core.versionManager.deleteVersion.actionCanceled",
          "info"
        );
      }
      let deleteResult;
      this.$s.ui.globalSpinner.show();
      try {
        deleteResult = await this.asyncOps.delete(
          `entity/${this.computedVersionEntityType}`,
          { ids: [version.id] }
        );
      } catch(e) {

      }

      this.$s.ui.globalSpinner.hide();
      if (deleteResult.hasError) {
        return this.$s.ui.notification(
          "core.versionManager.deleteVersion.deleteSuccess",
          "deleteError"
        );
      }

      this.$emit("versionManager:versionDeleted", {versionManager:this, version})
      this.refreshAsyncData();
      this.$s.ui.notification(
        "core.versionManager.deleteVersion.deleteSuccess",
        "success"
      );
    },
    showVersion(version) {
      this.versionToShow = version;
    },
    closeViewVersionModal() {
      this.versionToShow = false;
    },
    async startAcceptVersionAndDeleteFlow(version) {
      try {
        await this.$s.ui.modal.confirm(
          "core.versionManager.deleteAndAcceptVersion.confirmText"
        );
      } catch (e) {
        return this.$s.ui.notification(
          "core.versionManager.deleteAndAcceptVersion.actionCanceled",
          "info"
        );
      }
      let acceptResult;
      this.$s.ui.globalSpinner.show();
      try {
        acceptResult = await this.asyncOps.patch(
          `entity/${this.entityType}/${this.id}/version/${version.id}/accept`,
          {  }
        );
      } catch(e) {

      }

      this.$s.ui.globalSpinner.hide();
      if (acceptResult.hasError) {
        return this.$s.ui.notification(
          "core.versionManager.deleteAndAcceptVersion.acceptVersionError",
          "acceptVersionError"
        );
      }

      this.refreshAsyncData();
      this.$s.ui.notification(
        "core.versionManager.deleteAndAcceptVersion.acceptVersionSuccess",
        "success"
      );

      this.$emit("versionManager:versionAccepted", {versionManager:this, version})


      let deleteResult;
      this.$s.ui.globalSpinner.show();
      try {
        deleteResult = await this.asyncOps.delete(
          `entity/${this.computedVersionEntityType}`,
          { ids: [version.id] }
        );
      } catch(e) {

      }

      this.$s.ui.globalSpinner.hide();
      if (deleteResult.hasError) {
        return this.$s.ui.notification(
          "core.versionManager.deleteAndAcceptVersion.deleteSuccess",
          "deleteError"
        );
      }

      this.$emit("versionManager:versionDeleted", {versionManager:this, version})

      this.$s.ui.notification(
        "core.versionManager.deleteAndAcceptVersion.deleteSuccess",
        "success"
      );

      this.versionToShow = false;
      this.refreshAsyncData();

    },
    async startAcceptVersionFlow(version) {
      try {
        await this.$s.ui.modal.confirm(
          "core.versionManager.acceptVersion.confirmText"
        );
      } catch (e) {
        return this.$s.ui.notification(
          "core.versionManager.acceptVersion.actionCanceled",
          "info"
        );
      }
      let acceptResult;
      this.$s.ui.globalSpinner.show();
      try {
        acceptResult = await this.asyncOps.patch(
          `entity/${this.entityType}/${this.id}/version/${version.id}/accept`,
          {  }
        );
      } catch(e) {

      }

      this.$s.ui.globalSpinner.hide();
      if (acceptResult.hasError) {
        return this.$s.ui.notification(
          "core.versionManager.acceptVersion.acceptVersionError",
          "acceptVersionError"
        );
      }

      this.refreshAsyncData();
      this.$s.ui.notification(
        "core.versionManager.acceptVersion.acceptVersionSuccess",
        "success"
      );

      this.$emit("versionManager:versionAccepted", {versionManager:this, version})
      this.versionToShow = false;
    },
  },
  computed: {
    computedVersionEntityType() {
      if (this.versionEntityType) {
        return this.versionEntityType;
      }
      return `${this.entityType}Version`;
    },
    computedGetItemRoute() {
      if (typeof this.getItemUrl === "string") {
        return this.getItemUrl;
      }

      if (this.getItemUrl && typeof this.getItemUrl === "object") {
        return this.getItemUrl({ versionManager: this, ...this.$props });
      }

      // COMPUTE GET VERSIONED ENTITY URL

      return `entity/${this.entityType}/${this.id}/version/${this.versionToShow?.id}`;
    },
    computedGetFormPath() {
      if (typeof this.getFormConfigUrl === "string") {
        return this.getFormConfigUrl;
      }

      if (this.getFormConfigUrl && typeof this.getFormConfigUrl === "object") {
        return this.getFormConfigUrl({ versionManager: this, ...this.$props });
      }

      // COMPUTE GET VERSIONED ENTITY URL
      return `entity/${this.entityType}/${this.id}/update`;
    },
  },
};
</script>
